import { useCallback, useRef, useState } from 'react';
import type { WorkflowJob } from '../../graphql/types';
import { isTitleReviewFinalized } from '../rowMode';
import type { WorkspaceMode } from '../urlState';
import {
  FINALIZE_ERROR_MESSAGE,
  FINALIZE_PENDING_MESSAGE,
  persistReview,
} from './reviewFinalize';
import type { ReviewFinalizeInput } from './ReviewMode';

/**
 * Finalize handler for Review mode (Requirement 8.8).
 *
 * Persists every action and edited value through `finalizeDocumentReview`.
 * On success it merges the refreshed job into the Workspace, which unlocks
 * Findings and Title analysis (the Review_Gate reads the job's Document
 * Processing status, so it also holds after a reload), and opens Findings.
 * On failure it reports a plain-language error and leaves the reviewer's
 * actions alone: `ReviewMode` owns them and is not remounted.
 *
 * Finalize is gated by `isDocumentReviewed` / `isFinalizeEnabled` in
 * `ReviewMode` (`summarizeReview`), which only calls `onFinalize` when enabled.
 */
export interface UseFinalizeReviewOptions {
  jobId: string;
  updateJob: (job: WorkflowJob) => void;
  setMode: (mode: WorkspaceMode) => void;
}

export interface UseFinalizeReviewResult {
  finalize: (input: ReviewFinalizeInput) => Promise<void>;
  finalizing: boolean;
  finalizeError: string | null;
}

export function useFinalizeReview({
  jobId,
  updateJob,
  setMode,
}: UseFinalizeReviewOptions): UseFinalizeReviewResult {
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  // Guards against a double activation before `finalizing` has rendered.
  const inFlight = useRef(false);

  const finalize = useCallback(
    async ({ documents, files, actions }: ReviewFinalizeInput) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setFinalizing(true);
      setFinalizeError(null);
      try {
        const updated = await persistReview({ jobId, documents, files, actions });
        if (updated) updateJob(updated);
        if (updated && isTitleReviewFinalized(updated)) {
          setMode('findings');
        } else {
          setFinalizeError(FINALIZE_PENDING_MESSAGE);
        }
      } catch (err) {
        console.error('[ReviewMode] Finalize failed:', err);
        setFinalizeError(FINALIZE_ERROR_MESSAGE);
      } finally {
        inFlight.current = false;
        setFinalizing(false);
      }
    },
    [jobId, updateJob, setMode]
  );

  return { finalize, finalizing, finalizeError };
}
